import { StrKey } from '@stellar/stellar-sdk';
import axios from 'axios';

import { ICE_READ_TIMEOUT_MS } from 'constants/ice';

import { getIceLockerApiUrl } from 'helpers/url';

import type { ProxyAccountStats, ProxyDonor, ProxyIncomingSnapshot } from 'types/proxy-projection';

const STATS_BATCH_SIZE = 50;
const DELEGATORS_PAGE_SIZE = 100;

const fail = (): never => {
    throw new Error('Delegation data is unavailable. Please try again.');
};
const object = (value: unknown): Record<string, unknown> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return fail();
    return value as Record<string, unknown>;
};
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : fail());
const count = (value: unknown): number =>
    typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : fail();
const amount = (value: unknown): bigint =>
    typeof value === 'string' && /^(0|[1-9]\d*)$/.test(value) ? BigInt(value) : fail();
const isAddress = (value: unknown): value is string =>
    typeof value === 'string' &&
    (StrKey.isValidEd25519PublicKey(value) || StrKey.isValidContract(value));
const address = (value: unknown): string => (isAddress(value) ? value : fail());

// A 200 body carries "ready" or "stale" data and both are shown; the API answers 503 until its
// initial replay finishes.
const body = (raw: unknown): Record<string, unknown> => {
    const data = object(raw);
    if (data.status !== 'ready' && data.status !== 'stale') return fail();
    return data;
};
const stats = (data: Record<string, unknown>): ProxyAccountStats => ({
    upvoteIncomingStroops: amount(data.upvote_incoming_stroops),
    governIncomingStroops: amount(data.govern_incoming_stroops),
    upvoteDelegators: count(data.upvote_delegators),
    governDelegators: count(data.govern_delegators),
    uniqueDelegators: count(data.unique_delegators),
});

export const getProxyDirectoryStats = async (
    accounts: string[],
): Promise<Map<string, ProxyAccountStats>> => {
    const unique = [...new Set(accounts.filter(isAddress))];
    const batches: string[][] = [];
    for (let start = 0; start < unique.length; start += STATS_BATCH_SIZE) {
        batches.push(unique.slice(start, start + STATS_BATCH_SIZE));
    }
    const responses = await Promise.all(
        batches.map(batch =>
            axios.get<unknown>(`${getIceLockerApiUrl()}/proxy-stats/`, {
                params: { accounts: batch.join(',') },
                timeout: ICE_READ_TIMEOUT_MS,
            }),
        ),
    );
    return new Map(
        responses.flatMap(({ data }) =>
            list(body(data).results).map(raw => {
                const row = object(raw);
                return [address(row.account), stats(row)] as const;
            }),
        ),
    );
};

export const getProxyDelegators = async (owner: string): Promise<ProxyIncomingSnapshot> => {
    const url = `${getIceLockerApiUrl()}/accounts/${address(owner)}/proxy-delegators/`;
    const donors: ProxyDonor[] = [];
    let after: string | undefined;
    for (;;) {
        const { data: raw } = await axios.get<unknown>(url, {
            params: after
                ? { limit: DELEGATORS_PAGE_SIZE, after }
                : { limit: DELEGATORS_PAGE_SIZE },
            timeout: ICE_READ_TIMEOUT_MS,
        });
        const data = body(raw);
        for (const rawRow of list(data.results)) {
            const row = object(rawRow);
            donors.push({
                donor: address(row.donor),
                upvoteAmountStroops: amount(row.upvote_amount_stroops),
                governAmountStroops: amount(row.govern_amount_stroops),
            });
        }
        if (data.has_more === false) return { ...stats(data), donors };
        if (data.has_more !== true) return fail();
        // Donors are sorted by address, so a cursor that does not move forward is malformed.
        const next = address(data.next_after);
        if (after !== undefined && next <= after) return fail();
        after = next;
    }
};
